import {
  createContext,
  useCallback,
  useState,
  type FC,
  type ReactNode,
} from 'react'

import { ProductVariantId } from 'data/product'
import { getInventoryLevel, InventoryLevelResponse } from 'lib/api'
import { usePolling } from 'hooks/callback'

interface InventoryCtx {
  variantInventory: Record<ProductVariantId, number>
}

const POLLING_INTERVAL_MS = 1000 * 60 * 5

const empty: InventoryCtx = { variantInventory: {} }

export const InventoryContext = createContext(empty)

const reduceInventoryResponse = (r: InventoryLevelResponse) =>
  r.variants.reduce<InventoryCtx['variantInventory']>((acc, v) => {
    acc[v.admin_graphql_api_id] = v.inventory_quantity
    return acc
  }, {})

const InventoryProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const [inventoryState, setInventoryState] = useState(empty)

  const fetchAndSaveInventory = useCallback(() => {
    getInventoryLevel()
      .then((r) => {
        const variantInventory = reduceInventoryResponse(r)

        setInventoryState({ variantInventory })
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.warn("Couldn't get inventory level")
      })
  }, [])

  usePolling(fetchAndSaveInventory, POLLING_INTERVAL_MS)

  return (
    <InventoryContext.Provider value={inventoryState}>
      {children}
    </InventoryContext.Provider>
  )
}

export default InventoryProvider
